import { colors } from './const';
import { parseLayoutAuditFile, LAYOUT_AUDIT_FILENAME } from './layout-audit';
import {
  partitionMatrixLayoutIssues,
  readMatrixVariantsFromPlan,
  scanMatrixInventoryGap,
  scanMatrixLayoutIssuesStrict,
  scanInteractionIssues,
  scanLayoutFormatIssues,
} from './project-source-scan';
import { resolveProjectRoot } from './resolve-project-root';
import { formatLayoutBlockedFooter, formatPreviewReadyHandoffBlocks } from './workflow-prompts';
import { resolveLiveSurveyDevUrl } from './detect-framework';
import { writeLayoutVerifyPassed } from './workflow-state';

const c = colors;

export interface RunVerifyLayoutOptions {
  projectRoot?: string;
  fromFetch?: boolean;
}

function printMatrixGrepReminder(variants: ReturnType<typeof readMatrixVariantsFromPlan>): void {
  if (!variants.length) return;
  console.log(`${c.dim}Matrix grep block (each §3c row):${c.reset}`);
  console.log(`${c.dim}  grep -rn "nonNaScaleColumns" src/lib/surveyUi/ src/components/${c.reset}`);
  console.log(`${c.dim}  grep "nonNaScaleColumns" src/components/LikertMatrixScale.tsx${c.reset}`);
  console.log(
    `${c.dim}  grep -E "scaleAnchorLabels|hasNotApplicableOption|gridLayout.*carousel" src/components/CsatMatrixScale.tsx src/components/LikertMatrixScale.tsx${c.reset}`,
  );
  console.log(
    `${c.dim}  grep -E "justifyContent.*space-between|justify-between" src/components/CsatMatrixScale.tsx src/components/LikertMatrixScale.tsx${c.reset}`,
  );
  for (const v of variants) {
    console.log(
      `${c.dim}  §3c ${v.questionId}: ${v.type} displayStyle=${v.displayStyle} gridLayout=${v.gridLayout} hasNA=${v.hasNotApplicableOption}${c.reset}`,
    );
  }
}

export async function runVerifyLayout(options: RunVerifyLayoutOptions = {}): Promise<number> {
  const projectRoot = resolveProjectRoot(options.projectRoot);

  console.log(`${c.bold}=== CFM SDK — verify-layout ===${c.reset}`);
  console.log(`${c.dim}Project: ${projectRoot}${c.reset}\n`);

  const inventoryGap = scanMatrixInventoryGap(projectRoot);
  if (inventoryGap.length) {
    console.log(`${c.red}FAIL:${c.reset} Matrix inventory not populated:`);
    for (const m of inventoryGap) console.log(`  ${m}`);
    console.log(`\n${formatLayoutBlockedFooter()}\n`);
    return 1;
  }

  const variants = readMatrixVariantsFromPlan(projectRoot);
  const layoutIssues = scanMatrixLayoutIssuesStrict(projectRoot);
  const { blocking, nearMisses } = partitionMatrixLayoutIssues(layoutIssues);

  for (const m of nearMisses) {
    console.log(`${c.yellow}NEAR-MISS:${c.reset} ${m.slice('NEAR-MISS: '.length)}`);
  }

  if (blocking.length) {
    console.log(`${c.red}FAIL:${c.reset} Matrix layout structure issues:`);
    for (const m of blocking) console.log(`  ${m}`);
    console.log(
      `\n${c.dim}Fix: layout-cookbook.md · reverification-hooks.md · layout-tasks.md${c.reset}`,
    );
    console.log(`\n${formatLayoutBlockedFooter()}\n`);
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} Matrix layout structure (strict) passed`);

  const layoutFormatIssues = scanLayoutFormatIssues(projectRoot);
  if (layoutFormatIssues.length) {
    console.log(`${c.red}FAIL:${c.reset} Matrix layout format issues:`);
    for (const m of layoutFormatIssues) console.log(`  ${m}`);
    console.log(`\n${formatLayoutBlockedFooter()}\n`);
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} Matrix layout format (bipolar header / carousel header) passed`);

  const interactionIssues = scanInteractionIssues(projectRoot);
  if (interactionIssues.length) {
    console.log(`${c.red}FAIL:${c.reset} Interaction / format contract issues:`);
    for (const m of interactionIssues) console.log(`  ${m}`);
    console.log(`\n${formatLayoutBlockedFooter()}\n`);
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} Interaction contracts passed`);

  const audit = parseLayoutAuditFile(projectRoot);
  if (!audit.ok) {
    console.log(`${c.red}FAIL:${c.reset} ${LAYOUT_AUDIT_FILENAME} audit incomplete:`);
    for (const m of audit.errors) console.log(`  ${m}`);
    console.log(`\n${formatLayoutBlockedFooter()}\n`);
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} ${LAYOUT_AUDIT_FILENAME} — Broken = 0, all verdicts set`);

  printMatrixGrepReminder(variants);

  writeLayoutVerifyPassed(projectRoot);

  console.log(
    `\n${c.green}Layout conformance OK${c.reset} — safe for Phase 5b (visual skin) and preview handoff.`,
  );
  console.log(`${c.dim}Next: Phase 5b → npx cfm-sdk verify-build${c.reset}`);

  const { url: devUrl } = await resolveLiveSurveyDevUrl(projectRoot);
  console.log(`\n${formatPreviewReadyHandoffBlocks(devUrl)}\n`);

  return 0;
}
